import { motion, useReducedMotion } from "motion/react";

/**
 * A forward pass through a network, drawn.
 *
 * One component, two jobs: the landing sequence plays it once and slowly, and
 * every loading state in the site loops it small. Sharing the drawing is the
 * point — a site whose intro and whose spinner are the same object reads as
 * one idea, where an unrelated spinner would read as a stock component
 * someone dropped in.
 *
 * It is a depiction of inference rather than a themed progress bar: real
 * layers, real edges between them, and a signal that travels in one
 * direction. `speed` scales every duration together so the whole sequence
 * slows or quickens without the layers falling out of step.
 *
 * Always `aria-hidden`. Callers own the `role="status"` and the words — an
 * animation cannot tell anyone what is loading.
 */

/** 4 → 6 → 6 → 3. Shaped like a network, not like a decoration. */
const LAYERS = [4, 6, 6, 3];
const LAYER_GAP = 26;
const NODE_GAP = 13;

interface Point {
  x: number;
  y: number;
  layer: number;
}

function buildNetwork(): { points: Point[]; edges: [Point, Point][] } {
  const points: Point[] = [];
  const columns: Point[][] = [];

  LAYERS.forEach((count, layer) => {
    const column: Point[] = [];
    for (let i = 0; i < count; i += 1) {
      const point = {
        x: 12 + layer * LAYER_GAP,
        // Centred vertically, so layers of different sizes stay balanced
        // around one axis rather than hanging from the top.
        y: 50 + (i - (count - 1) / 2) * NODE_GAP,
        layer,
      };
      column.push(point);
      points.push(point);
    }
    columns.push(column);
  });

  const edges: [Point, Point][] = [];
  for (let layer = 0; layer < columns.length - 1; layer += 1) {
    for (const from of columns[layer]) {
      for (const to of columns[layer + 1]) edges.push([from, to]);
    }
  }
  return { points, edges };
}

const { points, edges } = buildNetwork();

const LAST_LAYER = LAYERS.length - 1;

export function NeuralAnimation({
  /** Seconds per layer. Everything else is derived from this. */
  layerDuration = 0.75,
  /** Loading states loop; the landing sequence plays once. */
  loop = false,
  className = "",
}: {
  layerDuration?: number;
  loop?: boolean;
  className?: string;
}) {
  const reduced = useReducedMotion() ?? false;

  // The sweep crosses every layer plus a beat to clear the output.
  const sweepDuration = layerDuration * (LAST_LAYER + 1.4);
  const cycle = sweepDuration + (loop ? 0.5 : 0);

  const repeat = loop ? Infinity : 0;

  return (
    <svg aria-hidden="true" viewBox="0 0 100 100" className={className}>
      {/* Edges draw layer by layer, so the signal visibly propagates forward
          instead of the whole graph appearing at once. */}
      <g stroke="var(--color-accent)" strokeWidth="0.3" opacity="0.55">
        {edges.map(([from, to], index) => (
          <motion.line
            key={`e${String(index)}`}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            initial={reduced ? false : { pathLength: 0, opacity: 0 }}
            animate={reduced ? { pathLength: 1, opacity: 1 } : { pathLength: 1, opacity: 1 }}
            transition={
              reduced
                ? { duration: 0 }
                : {
                    duration: layerDuration,
                    delay: 0.2 + from.layer * layerDuration,
                    ease: "easeOut",
                    repeat,
                    repeatDelay: loop ? cycle - layerDuration : 0,
                  }
            }
          />
        ))}
      </g>

      {/* Nodes light up as the signal reaches their layer. */}
      <g fill="var(--color-accent)">
        {points.map((point, index) => (
          <motion.circle
            key={`n${String(index)}`}
            cx={point.x}
            cy={point.y}
            r="1.9"
            initial={reduced ? false : { scale: 0, opacity: 0.25 }}
            animate={reduced ? { scale: 1, opacity: 1 } : { scale: [0, 1.6, 1], opacity: [0.25, 1, 1] }}
            transition={
              reduced
                ? { duration: 0 }
                : {
                    duration: layerDuration * 1.2,
                    delay: point.layer * layerDuration,
                    ease: [0.22, 1, 0.36, 1],
                    repeat,
                    repeatDelay: loop ? cycle - layerDuration * 1.2 : 0,
                  }
            }
            style={{ transformOrigin: `${String(point.x)}px ${String(point.y)}px` }}
          />
        ))}
      </g>

      {/* The activation front — a bright bar sweeping through the layers.
          This is what makes it read as propagation rather than as a graph
          assembling itself. */}
      {!reduced && (
        <motion.rect
          y="16"
          width="2.4"
          height="68"
          rx="1.2"
          fill="var(--color-glow)"
          initial={{ x: 6, opacity: 0 }}
          animate={{ x: [6, 96], opacity: [0, 1, 1, 0] }}
          transition={{
            duration: sweepDuration,
            delay: 0.2,
            ease: "linear",
            times: [0, 0.12, 0.85, 1],
            repeat,
            repeatDelay: loop ? cycle - sweepDuration : 0,
          }}
        />
      )}
    </svg>
  );
}
